/** 平台官网(设计图 01–04)的公开只读接口:/v1/site/* */
import type { ApiClient } from './client.js';
import type { Organizer, PublicityRow } from './contest.js';

export interface SiteOverview {
  productLines: number;
  courses: number;
  levels: number;
  contests: number;
  signupOpen: { site: string; name: string; signupEnd: string | null }[];
}
export interface CourseCard {
  id: string;
  name: string;
  track: string;
  trackLabel: string;
  intro: string | null;
  productLineId: string | null;
  levels: number;
  freeLevels: number;
  chapters: number;
  lessonHoursPerLevel: number;
  passThreshold: number;
}
export interface CatalogLine {
  id: string;
  code: string;
  name: string;
  tagline: string | null;
  intro: string | null;
  audience: string | null;
  freeParts: number;
  courses: CourseCard[];
  parts: {
    seq: number;
    name: string;
    knowledge: string;
    goal: string;
    assessmentKind: string;
    levelFrom: number | null;
    levelTo: number | null;
  }[];
}
export interface SiteCatalog {
  lines: CatalogLine[];
  courses: CourseCard[];
}
export interface SiteContest {
  site: string;
  name: string;
  intro: string | null;
  signupStart: string | null;
  signupEnd: string | null;
  state: 'soon' | 'signup' | 'running' | 'ended' | 'archived';
  organizers: Organizer[];
}
export interface SiteNotice {
  id: string;
  site: string;
  contestName: string;
  title: string;
  summary: string | null;
  kind: string;
  tag: string;
  publishedAt: string | null;
}
export interface BoardOptions {
  levels: { id: string; courseId: string; courseName: string; seq: number; name: string }[];
  exams: { id: string; name: string; subject: string; groups: string[]; publishedAt: string | null }[];
  contests: { id: string; title: string; contestName: string; publishedAt: string | null }[];
}
export interface BoardRow {
  rank: number;
  nickname: string;
  score: number;
  grade: string | null;
  durationSec: number | null;
  me?: boolean;
}
export interface ExamBoard {
  exam: { id: string; name: string };
  group: string | null;
  groups: { key: string; label: string }[];
  entries: BoardRow[];
}
export interface ContestBoard {
  title: string;
  contestName: string;
  site: string;
  entries: (PublicityRow & { awardLevel: string | null })[];
}

export function siteApi(api: ApiClient) {
  const S = '/v1/site';
  return {
    overview: () => api.get<SiteOverview>(`${S}/overview`),
    catalog: () => api.get<SiteCatalog>(`${S}/catalog`),
    contests: () => api.get<SiteContest[]>(`${S}/contests`),
    notices: () => api.get<SiteNotice[]>(`${S}/notices`),
    boardOptions: () => api.get<BoardOptions>(`${S}/boards`),
    levelBoard: (levelId: string, period: string) =>
      api.get<{ total: number; entries: BoardRow[] }>(`${S}/boards/level/${levelId}`, { query: { period } }),
    examBoard: (examId: string, group?: string) =>
      api.get<ExamBoard>(`${S}/boards/exam/${examId}`, { query: { group } }),
    contestBoard: (noticeId: string) => api.get<ContestBoard>(`${S}/boards/contest/${noticeId}`),
    /** 官网「加盟合作」留资(免登录;按 IP 与手机号限频) */
    partnerInquire: (body: PartnerInquiryInput) => api.post<{ id: string }>(`${S}/partner-inquiries`, body),
  };
}
export interface PartnerInquiryInput {
  name: string;
  phone: string;
  city: string;
  direction: 'org' | 'school' | 'channel' | 'other';
  message?: string;
  agreed: boolean;
}

/** 平台后台 · 合作咨询(平台管理员 / 运营) */
export interface PartnerInquiry {
  id: string;
  name: string;
  phoneMasked: string;
  city: string;
  direction: string;
  directionLabel: string;
  message: string | null;
  status: 'new' | 'contacted' | 'closed';
  statusLabel: string;
  note: string | null;
  handledAt: string | null;
  createdAt: string;
  fromSite: string;
}
export function partnerAdminApi(api: ApiClient) {
  const P = '/v1/platform/partner-inquiries';
  return {
    list: (status?: string, page = 1) =>
      api.get<{ items: PartnerInquiry[]; total: number; counts: Record<'new' | 'contacted' | 'closed', number> }>(
        P,
        { query: { status, page, size: 20 } },
      ),
    phone: (id: string) => api.get<{ id: string; phone: string | null }>(`${P}/${id}/phone`),
    update: (id: string, body: { status?: string; note?: string }) => api.patch<PartnerInquiry>(`${P}/${id}`, body),
  };
}
export type SiteApi = ReturnType<typeof siteApi>;
